import i18n, { getLocalizedConsumptionTaxName } from '@/utils/i18n';
import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  displayLineItemPrice,
  formatCurrency,
  ORDER_ITEM_PRICE_FIELDS,
  OrderFee,
} from '@/utils/money';
import { getGtinDescription, GtinSettings } from '@/utils/gtin-metadata';
import { getPackDisplayString } from '@/utils/item';
import { getParamsFromNameValue } from '@/utils/salt-item';
import { gql } from '@apollo/client';
import {
  groupBySubstitution,
  ItemWithSubstitute,
  OrderItemWithSubstitutionType,
  shouldRenderSeparatorForSubItems,
  SubstitutionItemWrapper,
} from './orders/substitution-item-wrapper';
import { GTINMetadata } from '@/contexts/item-context-types';
import { ItemSubtitle } from './item-subtitle';
import {
  LIST_ITEM_VERTICAL_PADDING,
  Props as ListItemProps,
  ListItemSeparator,
} from './list-item';
import {
  ListSection,
  RecyclerList,
  Props as RecyclerListProps,
} from './recycler-list';
import {
  OrderFeeDisplayType,
  OrderItemListFieldsFragment,
  OrderItemRatingFieldsFragment,
} from '@/types';
import { OrderFeeName, PackUnit } from '@/enums';
import { OrderItemListItem } from './order-item-list-item';
import { OrderItemType } from './orders/order-item-top-row';
import { SALT_ITEM_FIELDS_FRAGMENT } from '@/contexts/item-context-fragments';
import { ScrollViewProps, View } from 'react-native';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useOrderItemHorizontalSpace } from './orders/hooks/use-order-item-horizontal-space';
import { useUserContext } from '@/contexts/user-context';

export const ORDER_ITEM_RATING_FRAGMENT = gql`
  fragment OrderItemRatingFields on order_item_ratings {
    comment
    photo_url_list
    rating
    uuid
  }
`;

export const ORDER_ITEM_LIST_PACK_FIELDS_FRAGMENT = gql`
  fragment OrderItemListPackFields on packs {
    base_unit
    max_unit_size
    min_unit_size
    unit
    unit_count
    uuid
  }
`;

export const ORDER_ITEM_LIST_FRAGMENT = gql`
  fragment OrderItemListFields on orders_items {
    ...OrderItemPriceFields
    notes
    orders_items_modifier_options {
      name
      uuid
    }
    pack {
      ...OrderItemListPackFields
    }
    pricing_pack {
      ...OrderItemListPackFields
    }
    restaurant_display_name
    substitution
    variants_pack {
      external_item_id
      gtin_metadata @include(if: $skipSaltApi) {
        metadata
        uuid
      }
      item {
        description
        photo_url_list
        uuid
      }
      metadata
      prebook_config
      salt(locale: $locale, restaurantId: $restaurantUUID)
        @skip(if: $skipSaltApi) {
        ...ItemContext_SaltItemFields
      }
      uuid
    }
    uuid
  }

  ${ORDER_ITEM_LIST_PACK_FIELDS_FRAGMENT}
  ${ORDER_ITEM_PRICE_FIELDS}
  ${SALT_ITEM_FIELDS_FRAGMENT}
`;

export type OrderItemDataType = OrderItemListFieldsFragment & {
  notes?: string | null;
  order_item_rating?: OrderItemRatingFieldsFragment | null;
};

export type OrderItemListItemType =
  OrderItemWithSubstitutionType<OrderItemType>;

export interface Props {
  displayItemCode?: boolean;
  displayItemPrices?: boolean;
  displayPlaceholder?: boolean;
  extraData?: RecyclerListProps<OrderItemListItemType>['extraData'];
  fallbackCurrencyCode: string;
  gtinSettings?: GtinSettings;
  hideMissingItems?: boolean;
  hidePrices?: boolean;
  ListEmptyComponent?: RecyclerListProps<OrderItemListItemType>['ListEmptyComponent'];
  ListFooterComponent?: RecyclerListProps<OrderItemListItemType>['ListFooterComponent'];
  listItemProps?: Partial<ListItemProps<OrderItemListItemType>>;
  onNotesPress?: (note: string) => void;
  orderFees?: OrderFee[];
  orderItems: OrderItemDataType[];
  renderOrderItemFooter?: (option: OrderItemType) => JSX.Element | null;
  scrollViewProps?: ScrollViewProps;
  useInvoicePrices: boolean;
}

const SUBSTITUTION_HORIZONTAL_PADDING = 16;

export const OrderItemList: React.FC<Props> = ({
  displayItemCode = false,
  displayItemPrices = true,
  displayPlaceholder = true,
  extraData,
  gtinSettings,
  fallbackCurrencyCode,
  hideMissingItems = false,
  hidePrices,
  ListEmptyComponent,
  ListFooterComponent,
  listItemProps = {},
  onNotesPress,
  orderFees,
  orderItems,
  renderOrderItemFooter,
  scrollViewProps,
  useInvoicePrices,
}) => {
  const totalHorizontalSpace = useOrderItemHorizontalSpace();
  const [styles] = useStyles();
  const {
    selectedRestaurantRelationship: { consumptionTax },
  } = useUserContext();
  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const consumptionTaxName = consumptionTax
    ? getLocalizedConsumptionTaxName(consumptionTax[0].name)
    : null;

  const sections = useMemo<ListSection<OrderItemListItemType>[]>(() => {
    let foundCurrencyCode: string | null;
    const groupedOrderItems =
      groupBySubstitution<OrderItemDataType>(orderItems);

    const listItems = groupedOrderItems.reduce<OrderItemListItemType[]>(
      (listItemAcc: OrderItemListItemType[], orderItemGroup) => {
        // If there's an invoice, show invoice prices and quantities for all items
        const firstItem = orderItemGroup[0];

        const orderItemsForMetadata = orderItemGroup.reduce<
          ItemWithSubstitute<OrderItemType>[]
        >((metadataAcc, orderItem) => {
          const quantity = useInvoicePrices
            ? orderItem.order_item_prices?.pack_quantity_at_invoice
            : orderItem.order_item_prices?.pack_quantity_at_order;

          const nonPromoPrice = useInvoicePrices
            ? orderItem.order_item_prices?.unit_price_at_invoice_micros
            : orderItem.order_item_prices?.unit_price_at_order_micros;

          // pass discount price separately to display as discount in subtitle
          const discountPrice =
            orderItem.order_item_prices?.unit_discount_price_at_order_micros;

          if (!quantity && hideMissingItems) {
            return metadataAcc;
          }

          const {
            notes,
            order_item_rating,
            orders_items_modifier_options,
            restaurant_display_name,
            variants_pack,
            uuid,
          } = orderItem;

          foundCurrencyCode =
            foundCurrencyCode ||
            orderItem.order_item_prices?.currency_code ||
            null;

          const externalID = displayItemCode
            ? variants_pack.external_item_id
            : null;

          let description = variants_pack.item.description ?? null;
          let displayName = restaurant_display_name;
          // Use SALT information if enabled, otherwise use GTIN metadata if available
          if (saltApiIntegrationEnabled) {
            if (variants_pack.salt) {
              description =
                getParamsFromNameValue(
                  variants_pack.salt.basicInformation.descriptions,
                ).Description ?? description;
            }
          } else if (gtinSettings) {
            const gtinMetadata = (variants_pack.gtin_metadata?.[0]?.metadata ??
              null) as GTINMetadata | null;
            if (gtinMetadata) {
              description = getGtinDescription(
                gtinSettings,
                gtinMetadata,
                description,
              );
            }
          }
          const pack = orderItem.pricing_pack || orderItem.pack;

          const unitPriceInMicros =
            displayItemPrices && typeof nonPromoPrice === 'number'
              ? nonPromoPrice
              : null;

          // Price on invoice takes discounts into account, so only show discount price
          // if we're not using invoice prices
          const displayDiscountPrice =
            displayItemPrices &&
            !useInvoicePrices &&
            typeof discountPrice === 'number';

          metadataAcc.push({
            item: {
              metadata: {
                description,
                notes,
                orderItemRating: order_item_rating || null,
                quantity: quantity || 0,
                totalPrice: displayLineItemPrice(
                  {
                    currencyCode: foundCurrencyCode || fallbackCurrencyCode,
                    quantity: quantity || 0,
                    unitCount: pack.unit_count,
                    unitPriceInMicros: displayDiscountPrice
                      ? discountPrice
                      : displayItemPrices
                        ? nonPromoPrice
                        : null,
                  },
                  displayPlaceholder,
                ),
              },
              subtitle: (
                <ItemSubtitle
                  baseUnit={pack.base_unit as PackUnit | null}
                  currencyCode={foundCurrencyCode || fallbackCurrencyCode}
                  displayString={getPackDisplayString({
                    maxUnitSize: pack.max_unit_size,
                    minUnitSize: pack.min_unit_size,
                    unit: pack.unit as PackUnit,
                  })}
                  modifierString={orders_items_modifier_options
                    .map((o) => o.name)
                    .join(', ')}
                  promotionPriceInMicros={
                    displayItemPrices ? discountPrice : null
                  }
                  showInlineModifiers
                  sku={externalID}
                  unitCount={pack.unit_count}
                  unitPriceInMicros={unitPriceInMicros}
                />
              ),
              title: displayName,
              value: uuid,
            },
            substitution: orderItem.substitution,
          });
          return metadataAcc;
        }, []);

        // when hiding items excluded from the order, orderItemsForMetadata could be empty
        // so exclude it when building the top level list item
        if (orderItemsForMetadata.length) {
          listItemAcc.push({
            metadata: {
              orderItems: orderItemsForMetadata,
              substitutionAction: firstItem.substitution?.action || null,
              substitutionVariantPack:
                firstItem.substitution?.original_variant_pack,
            },
            title: firstItem.restaurant_display_name,
            value: firstItem.uuid,
          });
        }
        return listItemAcc;
      },
      [],
    );

    const feeListItems = (displayItemPrices ? orderFees || [] : []).reduce<
      OrderItemListItemType[]
    >((result, fee, index) => {
      const amountInMicros =
        fee.amount_at_invoice_micros ?? fee.amount_at_order_micros;
      if (
        fee.display_type === OrderFeeDisplayType.Fee &&
        typeof amountInMicros === 'number'
      ) {
        // The backend may provide us with tax, or consumption tax. As long as
        // consumption tax is enabled for the supplier, use that display name.
        const isTax = fee.name === OrderFeeName.TAX;
        const isConsumptionTax = consumptionTax
          ? fee.name === consumptionTax[0].name
          : false;
        const feeName =
          (isTax || isConsumptionTax) && consumptionTaxName
            ? consumptionTaxName
            : fee.name;
        const feeOrderItem: OrderItemType = {
          metadata: {
            description: null,
            isFee: true,
            notes: null,
            orderItemRating: null,
            quantity: 1,
            totalPrice: formatCurrency(
              amountInMicros,
              foundCurrencyCode || fallbackCurrencyCode,
            ),
          },
          title: feeName!,
          value: fee.uuid || `fee-${feeName}-${index}`,
        };
        result.push({
          metadata: {
            orderItems: [
              {
                item: feeOrderItem,
                substitution: null,
              },
            ],
            substitutionAction: null,
            substitutionVariantPack: null,
          },
          title: feeOrderItem.title,
          value: feeOrderItem.value,
        });
      }
      return result;
    }, []);

    return [{ data: listItems.concat(feeListItems), key: null }];
  }, [
    consumptionTax,
    consumptionTaxName,
    displayItemCode,
    displayItemPrices,
    displayPlaceholder,
    fallbackCurrencyCode,
    gtinSettings,
    hideMissingItems,
    orderFees,
    orderItems,
    saltApiIntegrationEnabled,
    useInvoicePrices,
  ]);

  const combinedListItemProps = useMemo<
    Partial<ListItemProps<OrderItemListItemType>>
  >(() => {
    return {
      ...listItemProps,
      renderMainContent: (topOption) => {
        const topLevelMetadata = topOption.metadata!;
        if (!topLevelMetadata) {
          return null;
        }
        const substitutionVP = topLevelMetadata.substitutionVariantPack;
        const substitutionAction = topLevelMetadata.substitutionAction;
        const numItems = topLevelMetadata.orderItems.length;

        const content = topLevelMetadata.orderItems.map((oi, idx) => {
          const listItem = oi.item;
          const metadata = listItem.metadata;

          return (
            <View key={listItem.value} testID="order-item-list-item">
              <OrderItemListItem
                description={metadata.description}
                footer={renderOrderItemFooter?.(listItem)}
                formattedPrice={hidePrices ? null : metadata.totalPrice}
                isFee={metadata.isFee}
                notes={metadata.notes}
                onNotesPress={onNotesPress}
                quantity={metadata.quantity}
                subtitle={listItem.subtitle}
                title={listItem.title}
              />
              {idx !== numItems - 1 ? (
                <ListItemSeparator style={styles.groupedItemSeparator} />
              ) : null}
            </View>
          );
        });

        return substitutionVP ? (
          <View
            style={[
              styles.listItemContainer,
              {
                marginHorizontal:
                  totalHorizontalSpace - SUBSTITUTION_HORIZONTAL_PADDING,
              },
            ]}
          >
            <SubstitutionItemWrapper
              action={substitutionAction}
              headerStyle={styles.substitutionHeader}
              noActionText={i18n.t('no_substitutes_selected')}
              originalVariantPack={substitutionVP}
              style={styles.substitutionStyle}
              textStyle={styles.substitutionText}
            >
              <View style={styles.innerSubWrapper}>{content}</View>
            </SubstitutionItemWrapper>
          </View>
        ) : (
          <View style={styles.listItemContainer}>{content}</View>
        );
      },
    };
  }, [
    hidePrices,
    listItemProps,
    onNotesPress,
    renderOrderItemFooter,
    styles,
    totalHorizontalSpace,
  ]);

  return (
    <RecyclerList
      estimatedItemSize={[200, 160, 160, 100]}
      extraData={extraData}
      ListEmptyComponent={ListEmptyComponent}
      ListFooterComponent={ListFooterComponent}
      listItemProps={combinedListItemProps}
      listItemSeparatorProps={{
        style: {
          marginHorizontal: totalHorizontalSpace,
        },
      }}
      sections={sections}
      shouldRenderSeparator={shouldRenderSeparatorForSubItems}
      {...scrollViewProps}
    />
  );
};

const useStyles = createStyles((theme) => ({
  groupedItemSeparator: {
    marginHorizontal: 0,
    marginVertical: LIST_ITEM_VERTICAL_PADDING,
  },
  innerSubWrapper: {
    paddingHorizontal: SUBSTITUTION_HORIZONTAL_PADDING,
    paddingVertical: LIST_ITEM_VERTICAL_PADDING,
  },
  listItemContainer: {
    flex: 1,
  },
  substitutionHeader: {
    backgroundColor: theme.colors.stroke,
  },
  substitutionStyle: {
    borderColor: theme.colors.stroke,
  },
  substitutionText: {
    color: theme.colors.primary,
  },
}));
